import React, { useState, useMemo } from 'react';
import Table from '../../components/Table/Table';
import DocumentTypeForm from '../../components/HR/DocumentTypeForm';
import { PageHeader, FilterBar, ErrorState, type FilterConfig } from '../../components/Common';
import { DocumentType, TableColumn } from '../../types';
import { exportToExcel, printAsExcel } from '../../utils/exportToExcel';
import {
  useDocumentTypes,
  useCreateDocumentType,
  useUpdateDocumentType,
  useDeleteDocumentType
} from '../../hooks/useDocumentTypes';
import { useToast } from '../../contexts/ToastContext';

const DocumentSettings: React.FC = () => {
  const { showToast } = useToast();
  const [showForm, setShowForm] = useState(false);
  const [editingDocumentType, setEditingDocumentType] = useState<DocumentType | undefined>();
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [requiredFilter, setRequiredFilter] = useState<string>('all');
  const [notificationDaysFilter, setNotificationDaysFilter] = useState<string>('all');

  const queryOptions = useMemo(() => ({
    sort: [{ field: 'createdAt', order: 'desc' as const }],
    pagination: { pageSize: 100 },
  }), []);

  const { data: documentTypes = [], isLoading, error, refetch } = useDocumentTypes(queryOptions);

  const createMutation = useCreateDocumentType(() => {
    showToast('Document type created successfully', 'success');
    refetch();
    setShowForm(false);
    setEditingDocumentType(undefined);
  });

  const updateMutation = useUpdateDocumentType(() => {
    showToast('Document type updated successfully', 'success');
    refetch();
    setShowForm(false);
    setEditingDocumentType(undefined);
  });

  const deleteMutation = useDeleteDocumentType(() => {
    showToast('Document type deleted successfully', 'success');
    refetch();
  });

  const uniqueCategories = Array.from(new Set(documentTypes.map(dt => dt.category).filter(Boolean)));

  const filteredDocumentTypes = useMemo(() => {
    return documentTypes.filter(docType => {
      const matchesSearch = !searchTerm || (
        docType.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (docType.description && docType.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
        docType.category.toLowerCase().includes(searchTerm.toLowerCase())
      );
      const matchesCategory = categoryFilter === 'all' || docType.category === categoryFilter;
      const matchesRequired = requiredFilter === 'all' ||
        (requiredFilter === 'required' && docType.isRequired) ||
        (requiredFilter === 'optional' && !docType.isRequired);
      const matchesNotificationDays = notificationDaysFilter === 'all' ||
        (notificationDaysFilter === '30' && docType.notificationDays === 30) ||
        (notificationDaysFilter === '60' && docType.notificationDays === 60) ||
        (notificationDaysFilter === '90' && docType.notificationDays === 90);
      return matchesSearch && matchesCategory && matchesRequired && matchesNotificationDays;
    });
  }, [documentTypes, searchTerm, categoryFilter, requiredFilter, notificationDaysFilter]);

  const handleExport = () => {
    const exportData = filteredDocumentTypes.map(docType => ({
      'Document Name': docType.name,
      'Category': docType.category,
      'Notification Days': docType.notificationDays,
      'Required': docType.isRequired ? 'Yes' : 'No',
      'Description': docType.description
    }));
    exportToExcel(exportData, `document_types_${new Date().toISOString().split('T')[0]}`);
  };

  const handlePrint = () => {
    const exportData = filteredDocumentTypes.map(docType => ({
      'Document Name': docType.name,
      'Category': docType.category,
      'Notification Days': docType.notificationDays,
      'Required': docType.isRequired ? 'Yes' : 'No',
      'Description': docType.description
    }));
    printAsExcel(exportData, 'Document Types');
  };

  const handleEdit = (documentType: DocumentType) => {
    setEditingDocumentType(documentType);
    setShowForm(true);
  };

  const handleDelete = async (documentType: DocumentType) => {
    if (window.confirm(`Are you sure you want to delete document type "${documentType.name}"?`)) {
      try {
        await deleteMutation.execute(documentType);
      } catch (err) {
        console.error('Delete document type error:', err);
      }
    }
  };

  const handleAdd = () => {
    setEditingDocumentType(undefined);
    setShowForm(true);
  };

  const handleFormSubmit = async (data: Partial<DocumentType>) => {
    if (editingDocumentType) {
      try {
        const updatedData = { ...editingDocumentType, ...data };
        await updateMutation.execute({ id: parseInt(editingDocumentType.id), data: updatedData });
      } catch (err) {
        console.error('Update document type error:', err);
      }
    } else {
      try {
        await createMutation.execute(data);
      } catch (err) {
        console.error('Create document type error:', err);
      }
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingDocumentType(undefined);
  };

  const filterConfigs: FilterConfig[] = [
    {
      key: 'category',
      label: 'Category',
      type: 'select',
      value: categoryFilter,
      onChange: setCategoryFilter,
      options: [{ value: 'all', label: 'All Categories' }, ...uniqueCategories.map(c => ({ value: c, label: c }))]
    },
    {
      key: 'required',
      label: 'Required',
      type: 'select',
      value: requiredFilter,
      onChange: setRequiredFilter,
      options: [
        { value: 'all', label: 'All' },
        { value: 'required', label: 'Required' },
        { value: 'optional', label: 'Optional' }
      ]
    },
    {
      key: 'notificationDays',
      label: 'Notification Days',
      type: 'select',
      value: notificationDaysFilter,
      onChange: setNotificationDaysFilter,
      options: [
        { value: 'all', label: 'All' },
        { value: '30', label: '30 Days' },
        { value: '60', label: '60 Days' },
        { value: '90', label: '90 Days' }
      ]
    }
  ];

  const columns: TableColumn[] = [
    { key: 'name', label: 'Document Name', sortable: true },
    { key: 'category', label: 'Category', sortable: true, render: (value: string) => (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        value === 'employee' ? 'bg-blue-100 text-blue-800' :
        value === 'car' ? 'bg-green-100 text-green-800' : 'bg-purple-100 text-purple-800'
      }`}>{value}</span>
    )},
    { key: 'notificationDays', label: 'Notification Days', sortable: true, render: (value: number) => `${value} days` },
    { key: 'isRequired', label: 'Required', sortable: true, render: (value: boolean) => (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${value ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'}`}>
        {value ? 'Required' : 'Optional'}
      </span>
    )},
    { key: 'description', label: 'Description', sortable: false }
  ];

  if (showForm) {
    return (
      <div className="space-y-6">
        <DocumentTypeForm
          documentType={editingDocumentType}
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
        />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ocean-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading document types...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <ErrorState message="Error loading document types" onRetry={() => refetch()} entityName="document types" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Document Settings"
        description="Configure document types and expiry notification settings"
      />

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-blue-700">
              Document types configured here will be used for tracking expiry dates and sending notifications.
              The notification days setting determines how many days before expiry to send alerts.
            </p>
          </div>
        </div>
      </div>

      <FilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search document types..."
        filters={filterConfigs}
        onClearFilters={() => {
          setSearchTerm('');
          setCategoryFilter('all');
          setRequiredFilter('all');
          setNotificationDaysFilter('all');
        }}
        totalCount={documentTypes.length}
        filteredCount={filteredDocumentTypes.length}
        entityName="document types"
        onAdd={handleAdd}
        addLabel="Add Document Type"
        onExport={handleExport}
        onPrint={handlePrint}
      />

      <Table
        columns={columns}
        data={filteredDocumentTypes}
        onEdit={handleEdit}
        onDelete={handleDelete}
        title="Document Types"
        showAddButton={false}
        searchable={false}
      />
    </div>
  );
};

export default DocumentSettings;
